import type { ReactNode } from "react";
import { classNames } from "./internal/class-names";

export type OperationalIconName =
  | "archive"
  | "capability-resources"
  | "arrow-left"
  | "arrow-right"
  | "check"
  | "chevron-down"
  | "chevron-up"
  | "circle"
  | "clock-end"
  | "clock-start"
  | "close"
  | "dictation"
  | "direct-message"
  | "drag"
  | "edit"
  | "file"
  | "filter"
  | "folder-add"
  | "fire"
  | "info"
  | "incident"
  | "location"
  | "microphone"
  | "more"
  | "open"
  | "organization"
  | "photo"
  | "read-only"
  | "refresh"
  | "requirement-add"
  | "requirements"
  | "reply"
  | "restore"
  | "role"
  | "save"
  | "search"
  | "send"
  | "shield"
  | "sort"
  | "stop"
  | "trash"
  | "tactical-add"
  | "upload"
  | "users"
  | "warning"
  | "water"
  | "workspace"
  | "bolt";

/**
 * Small, code-native operational icon set.
 *
 * Icons are deliberately decorative: controls and status components provide
 * their accessible names as visible text or `aria-label`. Keeping one stroke
 * vocabulary here prevents platform emoji and font glyphs from changing shape
 * or colour between Windows, Android, and iOS.
 */
export function OperationalIcon({
  className,
  name,
}: {
  readonly className?: string | undefined;
  readonly name: OperationalIconName;
}): ReactNode {
  const common = {
    "aria-hidden": true,
    className: classNames("thw-operational-icon", className),
    fill: "none",
    focusable: false,
    stroke: "currentColor",
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    strokeWidth: 1.9,
    viewBox: "0 0 24 24",
  };

  switch (name) {
    case "archive":
      return <svg {...common}><path d="M4 7h16v13H4zM3 4h18v3H3zM9 11h6" /></svg>;
    case "capability-resources":
      return <svg {...common}><circle cx="12" cy="5.5" r="3" /><path d="m10.7 5.5.9.9 1.8-2M12 8.5v3M4.5 14v-2.5h15V14M12 11.5V14" /><rect height="5" rx="1" width="5" x="2" y="15" /><rect height="5" rx="1" width="5" x="9.5" y="15" /><rect height="5" rx="1" width="5" x="17" y="15" /></svg>;
    case "arrow-left":
      return <svg {...common}><path d="m14.5 5-7 7 7 7M8 12h11" /></svg>;
    case "arrow-right":
      return <svg {...common}><path d="m9.5 5 7 7-7 7M16 12H5" /></svg>;
    case "check":
      return <svg {...common}><path d="m5 12.5 4.2 4.2L19 7" /></svg>;
    case "chevron-down":
      return <svg {...common}><path d="m6 9 6 6 6-6" /></svg>;
    case "chevron-up":
      return <svg {...common}><path d="m6 15 6-6 6 6" /></svg>;
    case "circle":
      return <svg {...common}><circle cx="12" cy="12" r="6.5" /></svg>;
    case "clock-end":
      return <svg {...common}><circle cx="12" cy="12" r="8.5" /><path d="M12 7v5l3.5 2M8 3h8M16.5 18.5 19 21" /></svg>;
    case "clock-start":
      return <svg {...common}><circle cx="12" cy="12" r="8.5" /><path d="M12 7v5l3.5 2M8 3h8M5 5 3 7" /></svg>;
    case "close":
      return <svg {...common}><path d="m6 6 12 12M18 6 6 18" /></svg>;
    case "dictation":
      return <svg {...common}><path d="M8 6a4 4 0 0 1 8 0v6a4 4 0 0 1-8 0zM5 11v1a7 7 0 0 0 14 0v-1M12 19v3M8 22h8" /><path d="M3 5h2M3 9h2M19 5h2M19 9h2" /></svg>;
    case "direct-message":
      return <svg {...common}><path d="M20 15a3 3 0 0 1-3 3H9l-5 3v-6a3 3 0 0 1-1-2.2V8a3 3 0 0 1 3-3h11a3 3 0 0 1 3 3z" /><path d="M8 10h8M8 13h5" /></svg>;
    case "drag":
      return <svg {...common}><circle cx="8" cy="6" r="1" fill="currentColor" stroke="none" /><circle cx="16" cy="6" r="1" fill="currentColor" stroke="none" /><circle cx="8" cy="12" r="1" fill="currentColor" stroke="none" /><circle cx="16" cy="12" r="1" fill="currentColor" stroke="none" /><circle cx="8" cy="18" r="1" fill="currentColor" stroke="none" /><circle cx="16" cy="18" r="1" fill="currentColor" stroke="none" /></svg>;
    case "edit":
      return <svg {...common}><path d="M4 20h4l11-11-4-4L4 16zM13.5 6.5l4 4M4 20h16" /></svg>;
    case "file":
      return <svg {...common}><path d="M6 3h8l4 4v14H6zM14 3v5h4M9 13h6M9 17h6" /></svg>;
    case "filter":
      return <svg {...common}><path d="M3 5h18l-7 8v6l-4-2v-4z" /></svg>;
    case "folder-add":
      return <svg {...common}><path d="M3 6h7l2 2h9v11H3z" /><path d="M12 11v5M9.5 13.5h5" /></svg>;
    case "fire":
      return <svg {...common}><path d="M13 3c1 4-2 5-2 8 0 1.2.8 2 2 2 1.7 0 2.8-1.6 2.3-3.7 2.3 1.8 3.7 4.2 3.7 6.5A7 7 0 0 1 5 16c0-4.2 2.6-7.8 6.5-11-.2 2 .3 3.3 1.5 4.2" /></svg>;
    case "info":
      return <svg {...common}><circle cx="12" cy="12" r="9" /><path d="M12 11v6M12 7h.01" /></svg>;
    case "incident":
      return <svg {...common}><path d="M12 3 3 8v8l9 5 9-5V8zM3 8l9 5 9-5M12 13v8" /></svg>;
    case "location":
      return <svg {...common}><path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z" /><circle cx="12" cy="10" r="2.5" /></svg>;
    case "microphone":
      return <svg {...common}><rect height="11" rx="4" width="8" x="8" y="3" /><path d="M5 11a7 7 0 0 0 14 0M12 18v3M8 21h8" /></svg>;
    case "more":
      return <svg {...common}><circle cx="5" cy="12" r="1" fill="currentColor" stroke="none" /><circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" /><circle cx="19" cy="12" r="1" fill="currentColor" stroke="none" /></svg>;
    case "open":
      return <svg {...common}><path d="M14 4h6v6M20 4l-9 9" /><path d="M18 13v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h6" /></svg>;
    case "organization":
      return <svg {...common}><path d="M4 21V8h10v13M14 12h6v9M2 21h20M7 11h1M11 11h1M7 15h1M11 15h1M7 19h1M17 15h1M17 19h1M7 8V4h4v4" /></svg>;
    case "photo":
      return <svg {...common}><rect height="16" rx="2" width="20" x="2" y="4" /><circle cx="8" cy="9" r="2" /><path d="m4 18 5-5 3 3 2-2 6 4" /></svg>;
    case "read-only":
      return <svg {...common}><rect height="10" rx="2" width="16" x="4" y="10" /><path d="M8 10V7a4 4 0 0 1 8 0v3M12 14v2" /></svg>;
    case "refresh":
      return <svg {...common}><path d="M20 7v5h-5M4 17v-5h5M6.1 8.4A7 7 0 0 1 18.8 9M5.2 15A7 7 0 0 0 17.9 15.6" /></svg>;
    case "requirement-add":
      return <svg {...common}><path d="M5 3h12v8M5 3v18h8M8 8l1.5 1.5L12 7M8 14h3M18 14v6M15 17h6" /></svg>;
    case "requirements":
      return <svg {...common}><rect height="18" rx="1.5" width="14" x="5" y="3" /><path d="m8 8 1.5 1.5L12 7M14 9h2M8 14l1.5 1.5L12 13M14 15h2" /></svg>;
    case "reply":
      return <svg {...common}><path d="m10 7-6 5 6 5v-3h3c4 0 6 2 7 4-.2-5-2.8-8-7-8h-3z" /></svg>;
    case "restore":
      return <svg {...common}><path d="M4 7h16v13H4zM3 4h18v3H3z" /><path d="m9 14 3-3 3 3M12 11v6" /></svg>;
    case "role":
      return <svg {...common}><circle cx="12" cy="7.5" r="3.5" /><path d="M5 21a7 7 0 0 1 14 0M4 4l2 2M20 4l-2 2" /></svg>;
    case "save":
      return <svg {...common}><path d="M4 3h13l3 3v15H4zM8 3v6h8V3M8 21v-7h8v7" /></svg>;
    case "search":
      return <svg {...common}><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>;
    case "send":
      return <svg {...common}><path d="m3 4 18 8-18 8 3-8zM6 12h15" /></svg>;
    case "shield":
      return <svg {...common}><path d="M12 3 5 6v5c0 4.4 2.8 7.5 7 10 4.2-2.5 7-5.6 7-10V6z" /><path d="m9 12 2 2 4-4" /></svg>;
    case "sort":
      return <svg {...common}><path d="M7 4v16m0 0-3-3m3 3 3-3M17 20V4m0 0-3 3m3-3 3 3" /></svg>;
    case "stop":
      return <svg {...common}><rect height="12" rx="1.5" width="12" x="6" y="6" /></svg>;
    case "trash":
      return <svg {...common}><path d="M4 7h16M9 7V4h6v3M7 7l1 13h8l1-13M10 11v5M14 11v5" /></svg>;
    case "tactical-add":
      return <svg {...common}><path d="m12 3 8 8-8 8-8-8z" /><path d="M12 8v6M9 11h6" /></svg>;
    case "upload":
      return <svg {...common}><path d="M12 16V4m0 0L7 9m5-5 5 5M5 20h14" /></svg>;
    case "users":
      return <svg {...common}><circle cx="9" cy="8" r="3" /><circle cx="17" cy="9" r="2.5" /><path d="M3 20a6 6 0 0 1 12 0M14 16a5 5 0 0 1 7 4" /></svg>;
    case "warning":
      return <svg {...common}><path d="M12 3 2.8 20h18.4zM12 9v5M12 17.5v.1" /></svg>;
    case "water":
      return <svg {...common}><path d="M3 8c2 0 2 1.5 4 1.5S9 8 11 8s2 1.5 4 1.5S17 8 19 8s2 1.5 2 1.5M3 14c2 0 2 1.5 4 1.5S9 14 11 14s2 1.5 4 1.5 2-1.5 4-1.5 2 1.5 2 1.5" /></svg>;
    case "workspace":
      return <svg {...common}><path d="m12 3 9 5-9 5-9-5zM3 12.5l9 5 9-5M3 16.5l9 5 9-5" /></svg>;
    case "bolt":
      return <svg {...common}><path d="m13.5 2-8 12h6l-1 8 8-12h-6z" /></svg>;
  }
}
